/**
 * KNK-1.55 / FR-11a — add-on modules per Account, and the usage that will price them.
 *
 * The decisions, with no React, so they can be tested. The data is the database's (migration
 * 20261009a8): `account_modules_for(tenant)` and `account_module_usage(tenant, months)`, both
 * superadmin-only, written through `set_account_module(tenant, module, state, trial_ends_on)`.
 *
 * ⚠️ THIS IS NOT THE GATE. `account_has_module()` is, inside every function a module owns: Knock
 * Mode's 18 refusals answer KM001, and `require_account_module()` is the guard the other four
 * modules must call when they are built. What this file decides is what to RENDER.
 *
 * ⚠️ A trial's end date is its LAST day, inclusive, on the US Pacific calendar (the database's
 * rule, `account_entitlement_active`). `draftError` compares against the caller's idea of today
 * only to catch an obvious mistake early; the server's check is the one that counts.
 */
import { GATE_MODULE_KEYS, MODULE_KEY_PATTERN } from '@/lib/accountFeatures';

export const ACCOUNT_MODULE_KEYS = ['knock_mode', 'lead_intake', 'field_management', 'gamification', 'property_data'] as const;
/**
 * Any key the catalogue holds. FR-11a's five are named above; RBAC-6.1 added the five feature gates
 * (lib/accountFeatures.ts GATE_MODULE_KEYS), and FR-20 means the next one is a catalogue row with no
 * code change, so the type is the catalogue's own key format rather than a closed list.
 */
export type AccountModuleKey = string;

export type ModuleState = 'on' | 'off' | 'trial';
const STATES: readonly ModuleState[] = ['on', 'off', 'trial'];

export interface AccountModule {
  key: AccountModuleKey;
  name: string;
  description: string;
  state: ModuleState;
  /** YYYY-MM-DD, the trial's last day. Only for `trial`. */
  trialEndsOn: string | null;
  /** On, or a trial whose last day has not passed. The server computes it. */
  active: boolean;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface ModuleUsageMonth {
  /** YYYY-MM-01. */
  month: string;
  seededDoors: number;
  activeKnockingReps: number;
  paidDataLookups: number;
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);
const isKey = (v: unknown): v is AccountModuleKey => typeof v === 'string' && MODULE_KEY_PATTERN.test(v);
const isState = (v: unknown): v is ModuleState => typeof v === 'string' && (STATES as readonly string[]).includes(v);
const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Rows it cannot read are dropped, not guessed at; `active` is true only when the server said true. */
export function parseAccountModules(raw: unknown): AccountModule[] {
  if (!Array.isArray(raw)) return [];
  const out: AccountModule[] = [];
  for (const r of raw) {
    if (!isRecord(r) || !isKey(r.key) || !isState(r.state)) continue;
    const ends = str(r.trial_ends_on);
    out.push({
      key: r.key,
      name: str(r.name) ?? r.key,
      description: str(r.description) ?? '',
      state: r.state,
      trialEndsOn: ends && DATE.test(ends) ? ends : null,
      active: r.active === true,
      updatedAt: str(r.updated_at),
      updatedByName: str(r.updated_by_name),
    });
  }
  return out;
}

const count = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) && n >= 0 ? n : null;
};

/** Newest month first. PostgREST may send a bigint as a string. */
export function parseModuleUsage(raw: unknown): ModuleUsageMonth[] {
  if (!Array.isArray(raw)) return [];
  const out: ModuleUsageMonth[] = [];
  for (const r of raw) {
    if (!isRecord(r)) continue;
    const month = str(r.month);
    const seeded = count(r.seeded_doors);
    const reps = count(r.active_knocking_reps);
    const lookups = count(r.paid_data_lookups);
    if (!month || !DATE.test(month) || seeded === null || reps === null || lookups === null) continue;
    out.push({ month, seededDoors: seeded, activeKnockingReps: reps, paidDataLookups: lookups });
  }
  return out.sort((a, b) => b.month.localeCompare(a.month));
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "31 Oct 2026" from YYYY-MM-DD, without a Date: a calendar date has no timezone to drift in. */
export function formatDay(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

/** "Oct 2026" from YYYY-MM-01. */
export function formatMonth(ymd: string): string {
  const [y, m] = ymd.split('-').map(Number);
  return `${MONTHS[m - 1]} ${y}`;
}

export function moduleStateLine(m: AccountModule): string {
  if (m.state === 'on') return COPY.stateOn;
  if (m.state === 'off' || !m.trialEndsOn) return COPY.stateOff;
  return m.active ? `Trial, last day ${formatDay(m.trialEndsOn)}` : `Trial ended ${formatDay(m.trialEndsOn)}, so it is off`;
}

/** Today on the calendar the database uses for trials, as YYYY-MM-DD. */
export function pacificToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

export interface ModuleDraft {
  state: ModuleState;
  /** YYYY-MM-DD, or '' when none. */
  trialEndsOn: string;
}

/** The refusal the server would give, caught before the round trip. `today` is YYYY-MM-DD. */
export function draftError(d: ModuleDraft, today: string): string | null {
  if (d.state !== 'trial') return null;
  if (!DATE.test(d.trialEndsOn)) return COPY.errTrialNeedsDate;
  if (d.trialEndsOn < today) return COPY.errTrialPast;
  return null;
}

const COPY = {
  title: 'Add-on modules',
  intro:
    'What this account has bought, is trialling or has switched on. Only a Joules superadmin can change it. Off hides it from everyone in the account, its admins included; Joules superadmins still see it. Switching a module off hides it without deleting anything, and switching it back on restores it.',
  trialRule: 'A trial runs through its last day, US Pacific time, then switches itself off.',
  notBuiltYet: 'Not built yet. Switching it on has no visible effect until it ships.',
  stateOn: 'On',
  stateOff: 'Off',
  labels: { on: 'On', off: 'Off', trial: 'Trial' } satisfies Record<ModuleState, string>,
  lastDay: 'Last day',
  save: 'Save',
  saved: 'Saved',
  changedBy: (who: string | null, when: string) => `Changed ${when}${who ? ` by ${who}` : ''}`,
  errTrialNeedsDate: 'A trial needs a last day.',
  errTrialPast: 'A trial cannot end in the past.',
  usageTitle: 'Usage',
  usageIntro: 'Counted per month so a price can be set later. Nothing is billed from this yet.',
  usageCols: { month: 'Month', seeded: 'Doors seeded', reps: 'Reps knocking', lookups: 'Paid data lookups' },
  usageEmpty: 'No usage recorded.',
} as const;

export { COPY as ACCOUNT_MODULES_COPY };

/** Modules whose features exist today. The rest are entitlements waiting for their code. */
export const BUILT_MODULES: ReadonlySet<AccountModuleKey> = new Set<AccountModuleKey>(['knock_mode', ...GATE_MODULE_KEYS]);

/**
 * RBAC-6.1. Contracts and Solar Design are switched per account here, but who inside the account may
 * use them is still a role list in that account's settings until RBAC-6.2 retires the lists.
 */
export function moduleAccessNote(key: AccountModuleKey): string | null {
  if (key === 'contracts') return "Who can use it is still the Contracts access list in this account's settings.";
  if (key === 'solar_design') return "Who can use it is still the Design access list in this account's settings.";
  return null;
}
